import React, { useCallback } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  VERTICAL_SORTABLE_SCROLL_AREA_CLASS,
  canScrollVerticalSortableArea,
  getSortableMoveIndexes,
  restrictVerticalSortableMovement,
} from './verticalSortableUtils.js';
import './VerticalSortableList.scss';

const VERTICAL_SORTABLE_MODIFIERS = [restrictVerticalSortableMovement];
const VERTICAL_SORTABLE_AUTO_SCROLL = {
  canScroll: canScrollVerticalSortableArea,
};
const noop = () => {};

const getClassName = (...classNames) => classNames.filter(Boolean).join(' ');

export const VerticalSortableList = ({
  children,
  disabled = false,
  itemIds = [],
  onMove = noop,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      if (disabled || !over) return;

      const indexes = getSortableMoveIndexes(itemIds, active.id, over.id);
      if (!indexes) return;

      onMove({
        ...indexes,
        activeId: active.id,
        overId: over.id,
      });
    },
    [disabled, itemIds, onMove]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={VERTICAL_SORTABLE_MODIFIERS}
      autoScroll={VERTICAL_SORTABLE_AUTO_SCROLL}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};

export const useVerticalSortableItem = ({ disabled = false, id }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled,
  });

  return {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    style: {
      transform: CSS.Transform.toString(transform),
      transition,
    },
  };
};

export const VerticalSortableScrollArea = ({
  children,
  className = '',
  maxHeight,
}) => {
  const style = maxHeight
    ? { '--vertical-sortable-max-height': maxHeight }
    : undefined;

  return (
    <div
      className={getClassName(VERTICAL_SORTABLE_SCROLL_AREA_CLASS, className)}
      style={style}
    >
      {children}
    </div>
  );
};

export const SortableDragHandle = ({
  attributes,
  className = '',
  disabled = false,
  label,
  listeners,
}) => (
  <button
    type="button"
    className={getClassName('vertical-sortable-drag-handle', className)}
    disabled={disabled}
    aria-label={label}
    {...attributes}
    {...listeners}
  >
    <i className="bi bi-grip-vertical" aria-hidden="true" />
  </button>
);
